import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Tooltip from '../Tooltip/Tooltip'
import getShareOptions from './ShareOptions'
import getUpdateOptions from './UpdateOptions'
import { PLANS } from '../../constants/plans'
import { F_TRACKED_LINKS } from '../../constants/plans/constants'
import { hasFeature } from '../../utils/general'
import { ACTION_SHARE } from './OptionsModal'
import { GATED_MODE_EMAIL_WHITELIST } from '../UpdateSiteModal/constants'
import styles from './styles/OptionsModal.module.css'

const OptionsContent = ({ site, userProfile, action, onClose, onEditPDF }) => {
  const [showTooltip, setShowTooltip] = useState(action === ACTION_SHARE)
  const copyTargetRef = useRef(null)
  const dispatch = useDispatch()
  const history = useHistory()

  const url = `https://${site.subdomain}`
  const isLinkPreviewSite = !!site?.inPreviewMode
  const productId = useSelector((state) => state.manage.userProfile.productId) || null
  const hasAccess = useMemo(() => {
    return hasFeature(productId, F_TRACKED_LINKS)
  }, [productId])

  useEffect(() => {
    if (action === ACTION_SHARE && !isLinkPreviewSite) {
      setTimeout(() => copyUrl(), 500)
    }
  }, [action, isLinkPreviewSite])

  const copyUrl = (e) => {
    if (e) e?.preventDefault()
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setShowTooltip(true)
      })
      .catch((err) => {
        console.error('Failed to copy URL: ', err)
      })
  }

  const handleTooltipHide = () => {
    setShowTooltip(false)
  }

  const shareOptions = useMemo(
    () => getShareOptions({ url, hasAccess, dispatch, onClose, site, userProfile }),
    [url, hasAccess, site, userProfile]
  )

  const updateOptions = useMemo(
    () =>
      getUpdateOptions({
        isPDF: ['application/pdf', 'PDF'].includes(site?.type),
        isPHP: site.subdomain?.includes('tiiny.io'),
        isHTML:
          ['HTML', 'ZIP', 'EXAMPLE', 'text/html', 'application/zip'].includes(site?.type) &&
          !site.siteType?.includes('TEXT'),
        isCollection: site?.siteType?.includes('multiple'),
        isPasswordProtected: site?.passwordProtected,
        isEmailCaptureEnabled: site?.enableEmailGate || site?.gatedMode === 'Capture emails',
        isEmailWhitelistEnabled: site?.enableEmailWhitelist || site?.gatedMode === GATED_MODE_EMAIL_WHITELIST,
        dispatch,
        history,
        onClose,
        site,
        userProfile,
        onEditPDF
      }),
    [dispatch, history, site, userProfile, onEditPDF, onClose]
  )

  const options = action === ACTION_SHARE ? shareOptions : updateOptions
  const title = action === ACTION_SHARE ? 'Share' : 'Update'

  return (
    <div>
      <div className="bg-white rounded px-2 py-4">
        <h1 className="text-center h3 fw-bold mb-3">{title}</h1>
        {!isLinkPreviewSite && (
          <div className="text-center h4" ref={copyTargetRef} onClick={copyUrl} style={{ cursor: 'pointer' }}>
            <span className="black font-18">{url.replace('https://', '')}</span>
            {action === ACTION_SHARE && (
              <>
                <img
                  className={`ms-2 ${styles.copyIcon}`}
                  src="/assets/icons/export/copy.svg"
                  alt="Copy URL"
                  height="18"
                  width="18"
                />
                <Tooltip
                  active={showTooltip}
                  time={1500}
                  text="URL Copied!"
                  placement="bottom"
                  target={copyTargetRef.current}
                  onHide={handleTooltipHide}
                />
              </>
            )}
          </div>
        )}

        <div className="d-flex flex-column mx-3 mt-3">
          {options.map((option, index) => (option.isVisible ? <div key={index}>{option.component}</div> : null))}
        </div>
      </div>
    </div>
  )
}

export default OptionsContent
